import { FaArrowRight } from 'react-icons/fa6'
import styled from 'styled-components'
import useWindowWidth from '../../../utils/use.window.width'

interface ButtonProps {
	$tailPosition?: 'top-right' | 'bottom-right'
	$variant?: 'filled' | 'outline'
}

const COLORS = {
	green: '#018d47',
	white: '#ffffff',
	shadow: 'rgba(0, 0, 0, 0.15)',
}

const IconCircle = styled.div<{ $variant?: 'filled' | 'outline' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  left: 10px;
 width: 28px;
 height: 28px;
  border-radius: 50%;
  background-color: ${props => props.$variant === 'outline' ? COLORS.white : COLORS.green};
  z-index: 2;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  box-shadow: ${props => props.$variant === 'outline'
    ? `0 0 0 5px ${COLORS.green}, inset 0 3px 6px rgba(0,0,0,0.25), 0 6px 12px rgba(0,0,0,0.15)`
    : `0 0 0 5px ${COLORS.white}, inset 0 3px 6px rgba(0,0,0,0.25), 0 6px 12px rgba(0,0,0,0.15)`};

  svg {
    color: ${COLORS.white};
    transition: color 0.4s ease;
  }

  @media(max-width: 768px) {
   width: 22px;
   height: 21px;
  }
`

const ContentText = styled.span<{ $variant?: 'filled' | 'outline' }>`
	font-family: 'e-Ukraine', sans-serif;
	font-size: 16px;
	font-weight: 400;
	z-index: 1;
	color: ${props =>
		props.$variant === 'outline' ? '#6b6b6b' : COLORS.white};
	transition:
		color 0.4s ease,
		transform 0.5s ease;
	${props =>
		props.$variant === 'filled' &&  `translateX(25px)`};
	
	margin: ${props =>
		props.$variant === 'outline' ? '0 10px' : '0 10px 0 45px'};
	
	@media (max-width: 1024px) {
		font-size: 12px;
	}

	&:hover {
		color: ${props =>
		props.$variant === 'outline' ? COLORS.green : ''};
	}
`
const ButtonWrapper = styled.button<ButtonProps>`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    
    border-radius: ${props =>
        props.$variant === 'outline' 
            ? '100px 0 100px 100px' 
            : (props.$tailPosition === 'bottom-right' ? '100px 100px 0 100px' : '100px 0 100px 100px')
    };

    cursor: pointer;
    border: ${props =>
        props.$variant === 'outline' ? '1px solid #6b6b6b' : `6px solid ${COLORS.green}`};
    outline: none;
    overflow: hidden;
    padding: ${props =>
        props.$variant === 'outline' ? '16px 20px' : `12px 20px`};
    background-color: ${props =>
        props.$variant === 'outline' ? COLORS.white : COLORS.green};
    transition: all 0.4s ease;

    &:active {
        transform: scale(0.98);
    }

    @media (max-width: 768px) {
        padding: ${props => props.$variant === 'outline' ? '12px 20px' : `9px 20px`};
    }

    &:hover {
        background-color: ${COLORS.white};
        border-radius: ${props => 
            props.$variant === 'outline' ? '100px 0 100px 100px' : '100px'
        }; 

        border: ${props =>
            props.$variant === 'outline' ? `1px solid ${COLORS.green}` : `6px solid ${COLORS.green}`};

        ${IconCircle} {
            left: calc(100% - 40px);
            background-color: transparent;
            box-shadow: none;

            svg {
                color: ${COLORS.green}; 
            }
        }

        ${ContentText} {
            color: ${COLORS.green};
            transform: ${props => 
                props.$variant === 'outline' ? 'translateX(0)' : 'translateX(-15px)'
            };
        }
    }
`

interface ButtonComponentProps {
	text: string
	position: 'top-right' | 'bottom-right'
	href?: string
	target?: string
	rel?: string
	onClick?: () => void
	variant?: 'filled' | 'outline'
}

const ButtonComponent = ({
	text,
	position,
	href,
	target,
	onClick,
	rel,
	variant = 'filled',
}: ButtonComponentProps) => {
	const windowWidth = useWindowWidth()
	const isTablet = windowWidth <= 768

	return (
		<ButtonWrapper
			as={href ? 'a' : 'button'}
			href={href}
			target={target}
			rel={rel}
			onClick={onClick}
			$tailPosition={position}
			$variant={variant}
		>
			{variant ==  'filled' && 
			<IconCircle $variant={variant}>
				<FaArrowRight size={isTablet ? 18 : 23} />
			</IconCircle>
			}
			
			<ContentText $variant={variant}>{text}</ContentText>
		</ButtonWrapper>
	)
}

export default ButtonComponent
